import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { messagingApi, socialApi } from '../../support/supportApi';
import {
  normalizeMessagingUser,
  formatMessagingHandle,
  messagingPeerKey,
} from './messagingUserUtils';
import {
  filterPeopleByQuery,
  matchSubtitleForResult,
  mergeSearchResults,
} from './messagingSearchUtils';
import StartConversationButton from './StartConversationButton';
import MessagingPeopleSkeleton from './MessagingPeopleSkeleton';
import { applyProfileAvatarImgFallback } from '../profilePictureUrl';

const TABS = [
  { id: 'all', label: 'All' },
  { id: 'followers', label: 'Followers' },
  { id: 'following', label: 'Following' },
];

function sortConversationsForList(conversations = []) {
  return [...conversations].sort((a, b) => {
    const unreadDiff = (b.unreadCount || 0) - (a.unreadCount || 0);
    if (unreadDiff !== 0) return unreadDiff;
    const aTime = new Date(a.lastMessageAt || a.updatedAt || 0).getTime();
    const bTime = new Date(b.lastMessageAt || b.updatedAt || 0).getTime();
    return bTime - aTime;
  });
}

function formatListTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function PeopleRow({ user, active, loading = false, unread = false, subtitle, time, unreadCount, onClick, matchType }) {
  const normalized = normalizeMessagingUser(user);
  if (!normalized) return null;

  return (
    <button
      type="button"
      className={`messaging-people__row noOutline-text${active ? ' is-active' : ''}${
        loading ? ' is-loading' : ''
      }${unread ? ' has-unread' : ''}`}
      onClick={onClick}
      disabled={loading}
      aria-busy={loading || undefined}
    >
      <img
        src={normalized.profilePictureUrl}
        alt=""
        className="messaging-people__avatar"
        onError={(e) => {
          applyProfileAvatarImgFallback(e.currentTarget, {
            userId: normalized.userId,
            name: normalized.displayName,
          });
        }}
      />
      <span className="messaging-people__row-body">
        <span className="messaging-people__top">
          <span className="messaging-people__name">{normalized.displayName}</span>
          {loading ? (
            <span className="messaging-people__opening" aria-hidden>
              Opening…
            </span>
          ) : time ? (
            <span className="messaging-people__time">{time}</span>
          ) : null}
        </span>
        <span className="messaging-people__bottom">
          <span
            className={`messaging-people__subtitle${
              matchType === 'message' ? ' messaging-people__subtitle--match' : ''
            }`}
          >
            {subtitle || (normalized.handle ? formatMessagingHandle(normalized.handle) : 'Say hello')}
          </span>
          {unreadCount > 0 ? <span className="messaging-people__unread">{unreadCount}</span> : null}
        </span>
      </span>
    </button>
  );
}

/**
 * Left column: All conversations + Followers + Following tabs.
 */
export default function MessagingPeoplePanel({
  activeTab,
  onTabChange,
  conversations = [],
  conversationsLoading = false,
  selectedPeerKey = '',
  selectingPeerKey = '',
  viewerUserId = '',
  onSelectConversation,
  onSelectUser,
  onStartConversation,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialLoaded, setSocialLoaded] = useState({ followers: false, following: false });
  const [conversationSearchResults, setConversationSearchResults] = useState([]);
  const [profileSearchResults, setProfileSearchResults] = useState([]);
  const [apiSearching, setApiSearching] = useState(false);

  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;

  const loadSocial = useCallback(
    async (tab) => {
      if (!viewerUserId) return;
      if (tab === 'followers' && socialLoaded.followers) return;
      if (tab === 'following' && socialLoaded.following) return;
      setSocialLoading(true);
      try {
        if (tab === 'followers') {
          const data = await socialApi.followers({
            userId: viewerUserId,
            profileMode: 'personal',
            forMessaging: true,
            skip: 0,
            limit: 80,
          });
          setFollowers(data?.users || []);
          setSocialLoaded((prev) => ({ ...prev, followers: true }));
        } else if (tab === 'following') {
          const data = await socialApi.following({
            userId: viewerUserId,
            profileMode: 'personal',
            forMessaging: true,
            skip: 0,
            limit: 80,
          });
          setFollowing(data?.users || []);
          setSocialLoaded((prev) => ({ ...prev, following: true }));
        }
      } catch {
        if (tab === 'followers') setFollowers([]);
        if (tab === 'following') setFollowing([]);
      } finally {
        setSocialLoading(false);
      }
    },
    [viewerUserId, socialLoaded.followers, socialLoaded.following]
  );

  useEffect(() => {
    if (activeTab === 'followers' || activeTab === 'following') {
      loadSocial(activeTab);
    }
  }, [activeTab, loadSocial]);

  useEffect(() => {
    if (isSearching && (activeTab === 'followers' || activeTab === 'following' || activeTab === 'all')) {
      if (activeTab === 'followers' || activeTab === 'all') loadSocial('followers');
      if (activeTab === 'following' || activeTab === 'all') loadSocial('following');
    }
  }, [isSearching, activeTab, loadSocial]);

  useEffect(() => {
    if (trimmedQuery.length < 2) {
      setConversationSearchResults([]);
      setProfileSearchResults([]);
      setApiSearching(false);
      return undefined;
    }

    setApiSearching(true);
    const timer = setTimeout(async () => {
      try {
        const [conversationData, profileData] = await Promise.all([
          messagingApi.search({ q: trimmedQuery, limit: 40 }),
          socialApi.profileSearch({ q: trimmedQuery, limit: 40, forMessaging: true }),
        ]);
        setConversationSearchResults(conversationData?.results || []);
        const profiles = (profileData?.profiles || []).map((profile) => normalizeMessagingUser(profile));
        setProfileSearchResults(profiles.filter(Boolean));
      } catch {
        setConversationSearchResults([]);
        setProfileSearchResults([]);
      } finally {
        setApiSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [trimmedQuery, viewerUserId]);

  const conversationByPeerKey = useMemo(() => {
    const map = new Map();
    for (const conv of conversations) {
      const key = messagingPeerKey(conv?.otherUser);
      if (key) map.set(key, conv);
    }
    return map;
  }, [conversations]);

  const clientSearchResults = useMemo(() => {
    if (!isSearching) return [];
    return (
      filterPeopleByQuery({
        query: trimmedQuery,
        conversations,
        followers,
        following,
        activeTab,
      }) || []
    );
  }, [isSearching, trimmedQuery, conversations, followers, following, activeTab]);

  const mergedSearchResults = useMemo(() => {
    if (!isSearching) return [];
    return mergeSearchResults(conversationSearchResults, clientSearchResults, profileSearchResults);
  }, [isSearching, conversationSearchResults, clientSearchResults, profileSearchResults]);

  const sortedConversations = useMemo(
    () => sortConversationsForList(conversations),
    [conversations]
  );

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + (Number(c.unreadCount) || 0), 0),
    [conversations]
  );

  const isRowLoading = (peerKey) =>
    Boolean(selectingPeerKey && peerKey && String(peerKey) === String(selectingPeerKey));

  const handleSearchResultClick = (row) => {
    if (row.conversation?.conversationId) {
      onSelectConversation?.(row.conversation);
      return;
    }
    onSelectUser?.(row.user);
  };

  const renderSearchResults = () => {
    if (trimmedQuery.length >= 2 && apiSearching && !mergedSearchResults.length) {
      return <MessagingPeopleSkeleton rows={5} />;
    }
    if (!mergedSearchResults.length) {
      return <p className="messaging-people__empty">No matches for “{trimmedQuery}”.</p>;
    }
    return (
      <div className="messaging-people__list">
        {mergedSearchResults.map((row) => {
          const peerKey = messagingPeerKey(row.user);
          const conv = row.conversation;
          const subtitle = conv
            ? matchSubtitleForResult(conv)
            : normalizeMessagingUser(row.user)?.handle
              ? formatMessagingHandle(normalizeMessagingUser(row.user).handle)
              : 'Start a chat';
          return (
            <PeopleRow
              key={peerKey || row.user?.userId}
              user={row.user}
              active={selectedPeerKey && peerKey === String(selectedPeerKey)}
              loading={isRowLoading(peerKey)}
              unread={(conv?.unreadCount || 0) > 0}
              subtitle={subtitle}
              time={conv ? formatListTime(conv.lastMessageAt) : ''}
              unreadCount={conv?.unreadCount || 0}
              matchType={row.matchType}
              onClick={() => handleSearchResultClick(row)}
            />
          );
        })}
      </div>
    );
  };

  const renderAll = () => {
    if (conversationsLoading) return <MessagingPeopleSkeleton rows={6} />;
    if (!conversations.length) {
      return <p className="messaging-people__empty">No conversations yet.</p>;
    }
    return (
      <div className="messaging-people__list">
        {sortedConversations.map((conv) => {
          const peerKey = messagingPeerKey(conv?.otherUser);
          const unreadCount = conv.unreadCount || 0;
          return (
            <PeopleRow
              key={conv.conversationId || peerKey}
              user={conv.otherUser}
              active={selectedPeerKey && peerKey === String(selectedPeerKey)}
              loading={isRowLoading(peerKey)}
              unread={unreadCount > 0}
              subtitle={
                unreadCount > 0
                  ? `${unreadCount} unread · ${conv.lastMessagePreview || 'New message'}`
                  : conv.lastMessagePreview || 'No messages yet'
              }
              time={formatListTime(conv.lastMessageAt)}
              unreadCount={unreadCount}
              onClick={() => onSelectConversation?.(conv)}
            />
          );
        })}
      </div>
    );
  };

  const renderSocialList = (users, emptyLabel) => {
    if (socialLoading) return <MessagingPeopleSkeleton rows={6} />;
    if (!users.length) return <p className="messaging-people__empty">{emptyLabel}</p>;
    return (
      <div className="messaging-people__list">
        {users.map((user) => {
          const normalized = normalizeMessagingUser(user);
          const peerKey = messagingPeerKey(normalized);
          const conv = peerKey ? conversationByPeerKey.get(peerKey) : null;
          return (
            <PeopleRow
              key={peerKey || normalized?.userId}
              user={user}
              active={selectedPeerKey && peerKey === String(selectedPeerKey)}
              loading={isRowLoading(peerKey)}
              unread={(conv?.unreadCount || 0) > 0}
              subtitle={
                (conv?.unreadCount || 0) > 0
                  ? `${conv.unreadCount} unread · ${conv.lastMessagePreview || 'New message'}`
                  : conv?.lastMessagePreview ||
                    (normalized?.handle ? formatMessagingHandle(normalized.handle) : 'Start a chat')
              }
              time={conv ? formatListTime(conv.lastMessageAt) : ''}
              unreadCount={conv?.unreadCount || 0}
              onClick={() => {
                if (conv) onSelectConversation?.(conv);
                else onSelectUser?.(user);
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <aside className="messaging-people">
      <div className="messaging-people__header">
        <h1 id="messaging-people-panel-title" className="messaging-people__title">
          Messages
          {totalUnread > 0 ? (
            <span className="messaging-people__title-unread">{totalUnread} unread</span>
          ) : null}
        </h1>
        {onStartConversation ? (
          <StartConversationButton
            showLabel={false}
            className="messaging-people__compose-btn"
            onClick={onStartConversation}
          />
        ) : null}
      </div>

      <div className="messaging-people__search">
        <FiSearch className="messaging-people__search-icon" aria-hidden />
        <input
          type="text"
          role="search"
          inputMode="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search names and messages"
          aria-label="Search names and messages"
          autoComplete="off"
        />
        {searchQuery ? (
          <button
            type="button"
            className="messaging-people__search-clear noOutline-text"
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
          >
            <FiX aria-hidden />
          </button>
        ) : null}
      </div>

      <div className="messaging-people__tabs" role="tablist" aria-label="Message lists">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`messaging-people__tab noOutline-text${activeTab === tab.id ? ' is-active' : ''}`}
            onClick={() => onTabChange?.(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="messaging-people__body" role="tabpanel">
        {isSearching ? (
          <>
            <p className="messaging-people__search-hint">
              {trimmedQuery.length < 2
                ? 'Type at least 2 characters to search message text.'
                : apiSearching
                  ? 'Searching…'
                  : `Results for “${trimmedQuery}”`}
            </p>
            {renderSearchResults()}
          </>
        ) : null}
        {!isSearching && activeTab === 'all' ? renderAll() : null}
        {!isSearching && activeTab === 'followers'
          ? renderSocialList(followers, 'No followers yet.')
          : null}
        {!isSearching && activeTab === 'following'
          ? renderSocialList(following, 'Not following anyone yet.')
          : null}
      </div>
    </aside>
  );
}
